/**
 * Feedback bridge — module-scope registry connecting ToastProvider to
 * FeedbackProvider across the provider tree.
 *
 * ToastProvider is intentionally mounted ABOVE SessionProvider/FeedbackProvider
 * (so toasts work while logged out), which means ToastItem can never reach
 * useFeedback() through context. FeedbackProvider registers its opener here on
 * mount; ToastItem subscribes via useSyncExternalStore so the error-toast
 * "Report" button renders only while feedback is actually reachable.
 */

import type { ErrorInfo } from '@/types/feedback';

type FeedbackOpener = (errorInfo: ErrorInfo) => void;

let opener: FeedbackOpener | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

/** Register the opener; returns an unregister cleanup for useEffect. */
export function registerFeedbackOpener(fn: FeedbackOpener): () => void {
  opener = fn;
  notify();
  return () => {
    if (opener === fn) {
      opener = null;
      notify();
    }
  };
}

export function subscribeFeedbackOpener(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getFeedbackOpener(): FeedbackOpener | null {
  return opener;
}
